import { defineStore } from 'pinia'

/**
 * Alert Store — manages alert notifications and unread counts.
 * Replaces the Vuex alert store (store/alert.js).
 *
 * Uses the AlertService which provides:
 * - filtered({ page, filter }) for listing alerts
 * - counts() for unread/total counts
 * - markRead(alert) to mark a single alert as read
 * - markAllRead() to mark all alerts as read
 */
export const useAlertStore = defineStore('alert', () => {
  const { $api } = useNuxtApp()

  const alerts = ref<any[]>([])
  const counts = ref<any>(null)
  const countsLoading = ref(false)
  const countsError = ref<unknown>(null)
  let countsPromise: Promise<void> | null = null

  /**
   * Fetch the first page of alerts.
   */
  const fetchAlerts = async () => {
    try {
      const response = await $api.alert.filtered({ page: 1 })
      if (response?.items) {
        alerts.value = response.items
      }
    } catch (e) {
      console.error('[useAlertStore] Failed to fetch alerts:', e)
    }
  }

  /**
   * Fetch alert counts (unread, total, etc.).
   */
  const fetchAlertCounts = async () => {
    if (countsPromise) return countsPromise

    countsLoading.value = true
    countsError.value = null
    countsPromise = (async () => {
      try {
        const response = await $api.alert.counts()
        if (response?.data) {
          counts.value = response.data
        }
      } catch (e) {
        countsError.value = e
        console.error('[useAlertStore] Failed to fetch alert counts:', e)
      } finally {
        countsLoading.value = false
        countsPromise = null
      }
    })()

    return countsPromise
  }

  /**
   * Mark a specific alert as read by its index in the alerts array.
   * Optimistically updates local state after a successful API call.
   */
  const markRead = async (index: number) => {
    const alert = alerts.value[index]
    if (!alert) return

    try {
      const response = await $api.alert.markRead(alert)
      if (response) {
        alerts.value[index] = { ...alerts.value[index], readForCurrentUser: true }
        if (counts.value && typeof counts.value.unread === 'number') {
          counts.value = { ...counts.value, unread: counts.value.unread - 1 }
        }
      }
    } catch (e) {
      console.error('[useAlertStore] Failed to mark alert as read:', e)
    }
  }

  const decrementUnreadCount = () => {
    if (counts.value && typeof counts.value.unread === 'number' && counts.value.unread > 0) {
      counts.value = { ...counts.value, unread: counts.value.unread - 1 }
    }
  }

  return {
    alerts,
    counts,
    countsLoading,
    countsError,
    fetchAlerts,
    fetchAlertCounts,
    markRead,
    decrementUnreadCount,
  }
})
